import { createContext, createRef, useContext, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { INLINE_NODE_VIEWS } from '../editorCapabilities/constants';
import uuid from './uuid';
import { jsx as _jsx } from "react/jsx-runtime";
const ReactNodeViewContext = /*#__PURE__*/createContext({
  node: undefined,
  view: undefined,
  getPos: undefined,
  decorations: undefined,
  editorConfig: undefined
});
class ReactNodeView {
  constructor(node, view, getPos, decorations, component, updateFn, editorConfig) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;
    this.decorations = decorations;
    this.component = component;
    this.componentRef = /*#__PURE__*/createRef();
    this.updateFn = updateFn;
    this.editorConfig = editorConfig;
    this.dom = document.createElement('div');
  }

  // Opt-in (via the INLINE_NODE_VIEWS editor-config flag): inline nodes render
  // in an inline container so an inline atom stays on the same line as the
  // surrounding text, instead of the default block <div> that forces a line
  // break. Scoped to editors that set the flag (e.g. the CRM activity creator);
  // every other editor keeps the existing <div> behavior.
  shouldRenderInline() {
    var _this$editorConfig;
    return this.node.isInline && Boolean((_this$editorConfig = this.editorConfig) === null || _this$editorConfig === void 0 ? void 0 : _this$editorConfig[INLINE_NODE_VIEWS]);
  }
  init() {
    this.dom = document.createElement(this.shouldRenderInline() ? 'span' : 'div');
    this.dom.classList.add('ProseMirror__dom');
    if (!this.node.isLeaf) {
      this.contentDOM = document.createElement('div');
      this.contentDOM.classList.add('ProseMirror__contentDOM');
      this.dom.appendChild(this.contentDOM);
    }
    return {
      nodeView: this,
      portal: this.renderPortal(this.dom)
    };
  }
  renderPortal(container) {
    const Component = props => {
      const componentRef = useRef(null);
      useEffect(() => {
        const componentDOM = componentRef.current;
        if (componentDOM != null && this.contentDOM != null) {
          if (!this.node.isLeaf && componentDOM.firstChild) {
            componentDOM.firstChild.appendChild(this.contentDOM);
          }
        }
      }, [componentRef]);
      const inner = /*#__PURE__*/_jsx(ReactNodeViewContext.Provider, {
        value: {
          node: this.node,
          view: this.view,
          getPos: this.getPos,
          decorations: this.decorations,
          editorConfig: this.editorConfig
        },
        children: /*#__PURE__*/_jsx(this.component, Object.assign({}, props))
      });

      // Match the outer container: an inline node view uses an inline wrapper so
      // it doesn't break onto its own line (block <div> in a paragraph).
      if (this.shouldRenderInline()) {
        return (
          /*#__PURE__*/
          // @bend-expect trellis-tokenized
          _jsx("span", {
            ref: componentRef,
            className: "ProseMirror__reactComponent",
            children: inner
          })
        );
      }
      return (
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx("div", {
          ref: componentRef,
          className: "ProseMirror__reactComponent",
          children: inner
        })
      );
    };
    return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/_jsx(Component, {}), container, uuid());
  }
  update(node, decorations) {
    if (this.updateFn) {
      return this.updateFn({
        previousNode: this.node,
        node,
        decorations
      });
    }
    return false;
  }
  destroy() {
    this.dom = document.createElement('div');
    this.contentDOM = undefined;
  }
}
export const createReactNodeView = ({
  node,
  view,
  getPos,
  decorations,
  component,
  onCreatePortal,
  updateFn,
  editorConfig
}) => {
  const reactNodeView = new ReactNodeView(node, view, getPos, decorations, component, updateFn, editorConfig);
  const {
    nodeView,
    portal
  } = reactNodeView.init();
  setTimeout(() => onCreatePortal(portal), 0);
  return nodeView;
};
export const useReactNodeView = () => useContext(ReactNodeViewContext);
export default ReactNodeView;